import {
    RadioGroup,
    styled,
    Table as MUITable,
    TableBody as MUITableBody,
    TableCell as MUITableCell,
    TableHead as MUITableHead,
    TableRow as MUITableRow,
    TableSortLabel as MUITableSortLabel,
} from '@mui/material';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import Box from 'shell/components/Box';
import Radio from 'shell/components/Radio';
import Tooltip from 'shell/components/Tooltip';
import { useDraggableScroll, useForkRef } from '../hooks';

const RootBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderLeft: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRight: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px 12px 0 0',
        background: theme.palette.ensightful.neutral[100],
    };
});

const WrapperBox = styled(Box)(({ theme, isDragging, enableDrag }) => {
    return {
        position: 'relative',
        borderRadius: '12px 12px 0 0',
        marginBottom: '100px',
        overflow: 'auto',
        whiteSpace: 'nowrap',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        cursor: enableDrag ? (isDragging ? 'grabbing' : 'grab') : 'default',
        userSelect: enableDrag ? 'none' : 'auto',
    };
});

const CustomTable = styled(MUITable)(() => {
    return {
        borderCollapse: 'separate',
    };
});

const TableHead = styled(MUITableHead)(() => {});

const TableSortLabel = styled(MUITableSortLabel)(() => {});

const TableBody = styled(MUITableBody)(() => {});

const TableRow = styled(MUITableRow)(() => {});

const TableCell = styled(MUITableCell)(({ isFixed, theme }) => {
    return {
        fontSize: `${theme.typography.p2.fontSize.large}rem`,
        whiteSpace: 'nowrap',
        '&.MuiTableCell-head': {
            background: theme.palette.ensightful.neutral[100],
            color: theme.palette.ensightful.neutral[700],
        },
        '&.MuiTableCell-body': {
            background: theme.palette.ensightful.shades[0],
        },
        '&:not(:last-child)': {
            borderRight: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        },
        ...(isFixed && {
            position: 'sticky',
            zIndex: 1,
            hyphens: 'auto',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
        }),
    };
});

const useTableSortableHeaderCellEffectHandlers = (
    data,
    columns,
    filters,
    setSortedData,
    handleFilters,
    extras = {}
) => {
    const [sortState, setSortState] = useState({
        orderBy: null,
        order: 'asc',
    });

    // data is all the SORTED data
    // sortedFilteredData takes the SORTED data and filters it down
    const [sortedFilteredData, setSortedFilteredData] = useState(data);

    const handleSetSortState = (newSortState) => {
        const column = columns.find((c) => c.id === newSortState.orderBy);
        if (column) {
            const sortedData = column.comparator(newSortState.order, data, extras);
            setSortedData(sortedData);
        }
        setSortState(newSortState);
    };

    // start with all data
    // sort all data using any sorts selected
    // filter out any data using any filters selected
    useEffect(() => {
        const allDataCopy = [...(data ? data : [])];
        handleFilters(allDataCopy, filters, setSortedFilteredData);
    }, [data, filters, setSortedFilteredData, handleFilters]);

    return { sortedFilteredData, sortState, handleSetSortState };
};

const CustomRadioGroup = styled(RadioGroup)(() => {
    return {
        '& label': {
            justifyContent: 'space-between',
        },
    };
});

const TableMobileSortFilters = ({ columns, sortState, handleSetSortState }) => {
    const buttons = useMemo(() => {
        return columns
            .filter((c) => !!c.comparator)
            .reduce((accum, c) => {
                const asc = {
                    ...c,
                    radioTitle: `${c.columnTitle} ${c.ascSuffix || '(A -> Z)'}`,
                    sortID: `${c.id}-asc`,
                };
                const desc = {
                    ...c,
                    radioTitle: `${c.columnTitle} ${c.descSuffix || '(Z -> A)'}`,
                    sortID: `${c.id}-desc`,
                };

                accum.push(asc, desc);
                return accum;
            }, []);
    }, [columns]);

    return (
        <CustomRadioGroup
            aria-label={'sort by'}
            value={`${sortState.orderBy}-${sortState.order}`}
            onChange={(_, value) => {
                const [orderBy, order] = value.split('-');
                handleSetSortState({ order, orderBy });
            }}
        >
            {buttons.map((b) => {
                return <Radio key={b.sortID} label={b.radioTitle} labelPlacement={'start'} value={b.sortID} />;
            })}
        </CustomRadioGroup>
    );
};

const TableSortableHeaderCell = styled(TableCell)(({ theme }) => {
    return {
        '& .MuiTableSortLabel-root': {
            justifyContent: 'space-between',
        },
        '&:not(._) > span': {
            color: theme.palette.ensightful.neutral[800],
            '& > *:hover': {
                color: theme.palette.ensightful.neutral[700],
            },
            '& > .table--active': {
                color: theme.palette.ensightful.neutral[900],
                '& > svg': {
                    color: theme.palette.ensightful.neutral[900],
                },
            },
        },
    };
});

const CustomTooltip = styled(Tooltip)(({ theme }) => {
    return {
        verticalAlign: 'inherit',
    };
});

const TableSortableHeaderCells = ({ columns, sortState, handleSetSortState }) => {
    const { order, orderBy } = sortState;

    const createClickHandler = (columnID) => {
        return () => {
            const isAsc = orderBy === columnID && order === 'asc';
            const newOrder = isAsc ? 'desc' : 'asc';
            handleSetSortState({
                order: newOrder,
                orderBy: columnID,
            });
        };
    };

    return (
        <>
            {columns.map((column) => {
                if (!column.comparator) {
                    return <TableSortableHeaderCell key={column.id}>{column.columnTitle}</TableSortableHeaderCell>;
                }

                const isActive = orderBy === column.id;

                return (
                    <TableSortableHeaderCell key={column.id} isFixed={column.isFixed}>
                        <TableSortLabel
                            sx={{ width: column.tooltip ? 'calc(100% - 24px)' : '100%' }}
                            className={isActive ? 'table--active' : null}
                            active={isActive}
                            direction={isActive ? order : 'asc'}
                            onClick={createClickHandler(column.id)}
                        >
                            {column.columnTitle}
                        </TableSortLabel>
                        {column.tooltip && <CustomTooltip content={column.tooltip} type={'info'} />}
                    </TableSortableHeaderCell>
                );
            })}
        </>
    );
};

/**
 * Main scrollable Table component.
 * Wraps the MUI Table in a styled container that supports optional drag-to-scroll.
 *
 * @param {Object} props
 * @param {string} [props.className] - CSS class for the root container.
 * @param {Object} [props.sx] - MUI SX system prop for styling the root container.
 * @param {boolean} [props.enableDrag=false] - If true, enables "grab and drag" scrolling functionality.
 * @param {React.ReactNode} props.children - The content of the table (TableHead, TableBody, etc.).
 */
const Table = forwardRef(({ className, sx, enableDrag = false, ...rest }, externalRef) => {
    const { ref: internalRef, events, isDragging } = useDraggableScroll();
    const tableRef = useForkRef(internalRef, externalRef);

    return (
        <RootBox className={className} sx={sx}>
            <WrapperBox
                tabIndex={0}
                ref={tableRef}
                isDragging={isDragging}
                enableDrag={enableDrag}
                {...(enableDrag ? events : {})}
            >
                <CustomTable {...rest} />
            </WrapperBox>
        </RootBox>
    );
});

export {
    Table as ScrollableTable,
    TableBody as ScrollableTableBody,
    TableCell as ScrollableTableCell,
    TableHead as ScrollableTableHead,
    TableMobileSortFilters as ScrollableTableMobileSortFilters,
    TableRow as ScrollableTableRow,
    TableSortableHeaderCells as ScrollableTableSortableHeaderCells,
    TableSortLabel as ScrollableTableSortLabel,
    useTableSortableHeaderCellEffectHandlers,
};
